import React from 'react';
import { BookOpen, ArrowRight, Brain, Lightbulb, Target, Zap } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { fetchPublishedModels } from '../../lib/api';
import SkeletonLoader from '../common/SkeletonLoader';

interface Model {
  id: string;
  name: string;
  description: string;
  content: string;
  tags: string[];
  created_at: string;
}

const FeaturedModelsSection: React.FC = () => {
  console.log('🧠 FeaturedModelsSection组件渲染');
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [models, setModels] = React.useState<Model[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  
  console.log('📊 FeaturedModelsSection状态:', {
    hasUser: !!user,
    authLoading,
    isLoading,
    hasError: !!error,
    modelsCount: models?.length || 0
  });

  React.useEffect(() => {
    fetchFeaturedModels();
  }, []);

  const fetchFeaturedModels = async () => {
    try {
      setIsLoading(true);
      const response = await fetchPublishedModels({ limit: 4 }); // 获取4个模型，显示3-4个
      if (response.success) {
        setModels(response.data?.models?.slice(0, 4) || []);
      } else {
        throw new Error(response.error || '获取模型失败');
      }
    } catch (error) {
      console.error('❌ FeaturedModelsSection错误:', error);
      setError(error instanceof Error ? error.message : '获取模型失败');
    } finally {
      setIsLoading(false);
    }
  };

  const getModelIcon = (tags: string[]) => {
    // 根据标签返回不同的图标
    if (tags.includes('思维') || tags.includes('认知')) return <Brain className="w-6 h-6 text-white" />;
    if (tags.includes('创新') || tags.includes('创意')) return <Lightbulb className="w-6 h-6 text-white" />;
    if (tags.includes('目标') || tags.includes('规划')) return <Target className="w-6 h-6 text-white" />;
    if (tags.includes('效率') || tags.includes('生产力')) return <Zap className="w-6 h-6 text-white" />;
    return <BookOpen className="w-6 h-6 text-white" />;
  };

  const handleModelClick = (modelId: string) => {
    // 所有用户都跳转到公开版本
    navigate(`/models/${modelId}`);
  };

  const handleViewMore = () => {
    navigate('/models');
  };

  if (error) {
    console.error('❌ FeaturedModelsSection错误:', error);
  }

  return (
    <section id="featured-models" className="py-12 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          {/* 增强的标题区域 */}
          <div className="inline-flex items-center px-4 py-2 bg-purple-50 border border-purple-200/50 rounded-full text-sm font-medium mb-4 shadow-sm">
            <div className="w-2 h-2 bg-purple-500 rounded-full mr-2 animate-pulse"></div>
            <span className="text-purple-700 font-semibold">思维模型工厂</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 via-blue-600 to-indigo-600">
              精选思维模型
            </span>
          </h2>
          
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            掌握经典思维框架，提升认知能力和决策水平
          </p>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <SkeletonLoader key={i} className="h-64" />
            ))}
          </div>
        ) : models && models.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
              {models.map((model) => (
                <div
                  key={model.id}
                  onClick={() => handleModelClick(model.id)}
                  className="bg-white rounded-lg shadow-sm hover:shadow-xl hover:border-blue-300 hover:-translate-y-1 transition-all duration-300 border cursor-pointer group overflow-hidden"
                >
                  {/* 顶部渐变条 */}
                  <div className="h-1 bg-gradient-to-r from-blue-500 to-blue-600"></div>
                  
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform duration-300">
                        {getModelIcon(model.tags)}
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 line-clamp-2 group-hover:text-blue-600 transition-colors duration-200 flex-1">
                        {model.name}
                      </h3>
                    </div>
                    
                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                      {model.description}
                    </p>
                    
                    {model.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {model.tags.slice(0, 2).map((tag, index) => (
                          <span
                            key={index}
                            className="inline-block px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                        {model.tags.length > 2 && (
                          <span className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">
                            +{model.tags.length - 2}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="text-center">
              <button
                onClick={handleViewMore}
                className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200 shadow-sm hover:shadow-md"
              >
                查看更多模型
                <ArrowRight className="ml-2 w-4 h-4" />
              </button>
            </div>
          </>
        ) : (
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-6">
              <BookOpen className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">暂无模型数据</h3>
            <p className="text-gray-500">精彩思维模型即将上线，敬请期待</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default FeaturedModelsSection;